import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import { deepFreeze, timestampToDate } from "../util/misc";
import { router } from "../router";
import { DialogRouter } from "../boot/dialogs";
import { forEach, cloneDeep } from "lodash";
import Address from "./Address";

export const DELIVERY_STATUS_FIELDS = {
  type: null,
  isTemporary: false,
  shouldInvoiceAnyway: false, // only applicable to temporary deliveries
  notifyCustomer: false, // only applicable to temporary deliveries - overrides noNotify for loading/transit
  // completedStops holds the log of each completed stop's timestamps.
  // The flat fields (loadingStarted, departed, etc.) represent the CURRENT stop.
  // currentStopIndex = completedStops.length (0 = warehouse, 1 = first temp stop, etc.)
  completedStops: [],
  loaded: {},
  delivered: {},
  deliveryVehicle: null,
  loadingStarted: null,
  loadingCompleted: null,
  departed: null,
  arrived: null,
  unloaded: null,
  undeliveredUnloaded: false,
  signature: null,
  signerName: null,
  noSignature: false,
  invoice: null,
  // Additional sales' invoices on a combined delivery, keyed by sale stateID.
  additionalInvoices: {},
  // Invoice numbers handed out for this event, keyed by sale stateID, written
  // by the getInvoiceID cloud function so a second creation for the same
  // items is refused. Registered here so a client status write cannot strip
  // it (sanitize drops unknown keys).
  invoiceClaims: {},
  proof: [],
  pdf: null,
};
deepFreeze(DELIVERY_STATUS_FIELDS);

function goToLoadingTruck() {
  let item = this._parent.item;
  if (item) {
    router.push({
      name: "loading-truck-delivery",
      params: {
        projectID: item.projectID,
        saleID: item.id,
        deliveryID: this._parent.id,
      },
    });
  }
}

function goToDelivery() {
  let item = this._parent.item;
  if (item) {
    router.push({
      name: "delivery",
      params: {
        projectID: item.projectID,
        saleID: item.id,
        deliveryID: this._parent.id,
      },
    });
  }
}

function unloadTruck() {
  DialogRouter.push({
    name: "delivery-unload",
    params: {
      deliveryID: this._parent.id,
    },
  });
}

function edit() {
  DialogRouter.push({
    name: "event-edit",
    params: {
      eventID: this._parent.id,
    },
  });
}

export const DELIVERY_STATUS_TYPES = {
  unload: {
    order: 0,
    label: "Unload",
    icon: "unloading",
    color: "negative",
    action: unloadTruck,
  },
  reschedule: {
    order: 1,
    label: "Reschedule",
    icon: "reschedule",
    color: "negative",
    action: edit,
  },
  ready: {
    order: 2,
    label: "Ready to load",
    icon: "loading_ready",
    color: "warning",
    action: goToLoadingTruck,
  },
  loading: {
    order: 3,
    label: "Loading onto truck",
    icon: "loading",
    color: "warning",
    time: "loadingStarted",
    action: goToLoadingTruck,
  },
  loaded: {
    order: 4,
    label: "Ready for transit",
    icon: "loaded",
    color: "positive",
    time: "loadingCompleted",
    action: goToDelivery,
  },
  transit: {
    order: 5,
    label: "In transit",
    icon: "delivering",
    color: "warning",
    time: "departed",
    action: goToDelivery,
  },
  unloading: {
    order: 6,
    label: "Unloading from truck",
    icon: "unloading",
    color: "warning",
    time: "arrived",
    action: goToDelivery,
  },
  deliveredPartial: {
    order: 7,
    label: "Partially delivered",
    icon: "delivered",
    color: "warning",
    time: "unloaded",
    action: goToDelivery,
  },
  delivered: {
    order: 8,
    label: "Delivered",
    icon: "delivered",
    color: "positive",
    time: "unloaded",
    action: goToDelivery,
  },
};
deepFreeze(DELIVERY_STATUS_TYPES);

export default class DeliveryStatus extends DataStructure {
  constructor(parent, data = {}) {
    const needsSanitize = Object.keys(DELIVERY_STATUS_FIELDS).some(
      (key) => !(key in data)
    );
    // Keep the parent's mutable data.status object shared so mutations and
    // computed type stay in sync. Clone frozen store data or sanitize raw
    // test/builder data when defaults/timestamp conversion are needed.
    if (Object.isFrozen(data) || needsSanitize) {
      data = sanitize(
        Object.isFrozen(data) ? cloneDeep(data) : data,
        DELIVERY_STATUS_FIELDS
      );
    }
    data.loadingStarted = timestampToDate(data.loadingStarted);
    data.loadingCompleted = timestampToDate(data.loadingCompleted);
    data.departed = timestampToDate(data.departed);
    data.arrived = timestampToDate(data.arrived);
    data.unloaded = timestampToDate(data.unloaded);

    super(data);

    this._parent = parent;
    // Always update type based on current conditions
    this.data.type = this.get();
  }

  get() {
    if (this._parent.data.rescheduleReason && this.quantityToUnload) {
      return "unload";
    } else if (this._parent.data.rescheduleReason) {
      return "reschedule";
    } else if (this.departed && !this.arrived) {
      // In transit - check this early since departed can be set without loadingStarted
      return "transit";
    } else if (this.arrived && !this.unloaded) {
      // Arrived at a stop
      if (!this.isAtFinalDestination) {
        // At an intermediate stop, ready to load more items
        return "ready";
      }
      return "unloading";
    } else if (this.unloaded) {
      // Delivery complete
      if (this.quantityToUnload > 0 || this.data.undeliveredUnloaded) {
        return "deliveredPartial";
      }
      return "delivered";
    } else if (!this.data.loadingStarted) {
      // Not started loading yet
      if (this.skipsWarehouseLoading) {
        // No items at warehouse, ready for transit to first temp stop
        return "loaded";
      }
      return "ready";
    } else if (!this.loadingCompleted) {
      return "loading";
    } else {
      // Loading completed, ready for transit
      return "loaded";
    }
  }

  // Multi-stop tracking

  get stops() {
    // Use the parent's stops getter which converts to Address objects
    return this._parent.stops || [];
  }

  get totalStops() {
    return this.stops.length + 1; // +1 for final destination
  }

  get currentStopIndex() {
    return (this.data.completedStops || []).length;
  }

  get isAtFinalDestination() {
    // currentStopIndex = completedStops.length
    // 0 = warehouse, 1 = first temp stop (stops[0]), ..., stops.length = last temp stop
    // If the index has advanced *past* the last temp stop there is no temp
    // stop here to load at, so we must be at the final destination regardless
    // of `arrived`. This also rescues deliveries left in a phantom stop when
    // `stops` was shrunk below `completedStops.length` (see EventEdit).
    if (this.currentStopIndex > this.stops.length) {
      return true;
    }
    // We're at final destination when:
    // - index >= stops.length (all temp stops completed), AND
    // - arrived is set (we've actually arrived, not just ready to load at last temp stop)
    // This distinguishes "at last temp stop ready to load" from "arrived at final destination"
    return this.currentStopIndex >= this.stops.length && this.arrived;
  }

  get currentStopAddress() {
    if (this.currentStopIndex === 0) {
      // At warehouse - no address object for warehouse
      return null;
    }
    if (this.isAtFinalDestination) {
      return this._parent.location || null;
    }
    // currentStopIndex 1 = stops[0], currentStopIndex 2 = stops[1], etc.
    return this.stops[this.currentStopIndex - 1] || null;
  }

  get nextStopAddress() {
    const nextIndex = this.currentStopIndex + 1;
    // nextIndex > stops.length means next stop is final destination
    if (nextIndex > this.stops.length) {
      return this._parent.location || null;
    }
    // nextIndex 1 = stops[0], nextIndex 2 = stops[1], etc.
    return this.stops[nextIndex - 1] || null;
  }

  get hasItemsAtCurrentStop() {
    const items = this._parent.items || [];
    const currentStopAddress = this.currentStopAddress;
    const currentStopIndex = this.currentStopIndex;

    return items.some((item) => {
      if (!item.itemLocation) return false;
      return item.itemLocation.some((loc) => {
        // Items on vehicle are always "at current stop" (can be unloaded)
        if (loc.data.type === "vehicle") return true;
        // At first stop, check for warehouse items
        if (currentStopIndex === 0 && loc.data.type === "warehouse")
          return true;
        // At temp stops, check for items at that temp location
        if (
          loc.data.type === "temp" &&
          currentStopAddress &&
          loc.data.address
        ) {
          const itemAddress = new Address(loc.data.address);
          return currentStopAddress.isEqual(itemAddress);
        }
        return false;
      });
    });
  }

  get hasItemsInWarehouse() {
    const items = this._parent.items || [];
    return items.some((item) => {
      if (!item.itemLocation) return false;
      return item.itemLocation.some((loc) => loc.data.type === "warehouse");
    });
  }

  // At the warehouse stop with nothing there to load: the first leg starts in
  // transit to the first temporary stop.
  get skipsWarehouseLoading() {
    return (
      this.currentStopIndex === 0 &&
      !this.data.loadingStarted &&
      !this.hasItemsInWarehouse
    );
  }

  get isInProgress() {
    // Delivery is in progress if it has started any stop (including ready after first stop)
    const statusType = this.get();
    const currentStopIndex = this.currentStopIndex;
    // 'ready' is in progress if we've completed at least one stop
    if (statusType === "ready" && currentStopIndex > 0) {
      return true;
    }
    return ["loading", "loaded", "transit", "unloading"].includes(statusType);
  }

  get isReadyForTransit() {
    // Ready for transit (or already in transit) to next stop if:
    // - Already in transit, OR
    // - loadingCompleted timestamp exists and we're not in 'loading' or 'ready' status, OR
    // - We're at warehouse (index 0) with no items there (skipped loading)
    const statusType = this.get();
    if (statusType === "transit") {
      return true;
    }
    const hasLoadingTimestamp =
      this.loadingCompleted &&
      statusType !== "loading" &&
      statusType !== "ready";
    const skippedWarehouseLoading =
      this.currentStopIndex === 0 &&
      statusType === "loaded" &&
      !this.loadingCompleted;
    return hasLoadingTimestamp || skippedWarehouseLoading;
  }

  // Formatting

  get deliveryVehicle() {
    return this.data.deliveryVehicle
      ? store.state.vehicles[this.data.deliveryVehicle]
      : null;
  }

  get loadedCount() {
    return Object.values(this.data.loaded).reduce(
      (total, count) => total + count,
      0
    );
  }

  get deliveredCount() {
    return Object.values(this.data.delivered).reduce(
      (total, count) => total + count,
      0
    );
  }

  get percentLoaded() {
    const numerator = this.loadedCount;
    const denominator = this._parent.quantity;
    const percent = (100 * numerator) / denominator;
    return { percent, numerator, denominator };
  }

  get percentDelivered() {
    const numerator = this.deliveredCount;
    const denominator = this._parent.quantity;
    const percent = (100 * numerator) / denominator;
    return { percent, numerator, denominator };
  }

  get itemsLoaded() {
    let items = {};
    forEach(this.data.loaded, (quantity, key) => {
      let id = key.split("-")[0];
      if (id in items) {
        items[id] += quantity;
      } else {
        items[id] = quantity;
      }
    });
    return items;
  }

  get itemsToUnload() {
    let items = {};
    forEach(this.data.loaded, (quantity, key) => {
      let id = key.split("-")[0];
      if (id in items) {
        items[id] += quantity;
      } else {
        items[id] = quantity;
      }
    });
    return items;
  }

  get quantityToUnload() {
    return Object.values(this.itemsToUnload).reduce(
      (total, count) => total + count,
      0
    );
  }

  get proof() {
    return this.data.proof.map((d) => new DocumentRef(d));
  }

  get pdf() {
    return this.data.pdf ? new DocumentRef(this.data.pdf) : null;
  }

  get invoice() {
    return this.data.invoice
      ? store.state.main.invoices[this.data.invoice]
      : null;
  }

  // Invoices raised for the *additional* sales of a combined delivery, keyed in
  // Firestore by sale stateID. `invoice` stays the primary sale's: its readers
  // all resolve it under the event's own sale, so an additional sale's invoice
  // number cannot live there.
  get additionalInvoices() {
    return Object.entries(this.data.additionalInvoices || {})
      .map(([saleStateID, invoiceID]) => ({
        saleStateID,
        projectID: saleStateID.slice(0, -1),
        saleID: saleStateID.slice(-1),
        invoice: store.state.main.invoices[invoiceID] || null,
      }))
      .filter((entry) => entry.invoice);
  }

  get time() {
    const type = DELIVERY_STATUS_TYPES[this.get()];
    return type?.time ? this.data[type.time] : null;
  }

  get type() {
    const type = DELIVERY_STATUS_TYPES[this.get()];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get action() {
    let type = this.type;
    return type && type.action ? type.action.bind(this) : null;
  }
}
